import { useState } from 'react';
import { FileText, MessageSquare, Send, Check, AlertCircle, Eye, Download } from 'lucide-react';
import EnhancedSignatureCapture from './EnhancedSignatureCapture';

/**
 * Client Final Signature View Component
 * 
 * Client-facing interface for:
 * - Viewing workflow progress (external-only status)
 * - Downloading final document
 * - Adding comments/feedback
 * - Requesting changes
 * - Signing final version
 */
export default function ClientFinalSignatureView({
  document,
  clientName,
  clientEmail,
  onAddFeedback,
  onSignDocument,
  isLoading = false,
}) {
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('comment');
  const [feedbackContent, setFeedbackContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  const handleAddFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackContent.trim()) {
      alert('Please enter your feedback');
      return;
    }

    try {
      await onAddFeedback({
        documentId: document._id,
        type: feedbackType,
        content: feedbackContent,
        clientName,
        clientEmail,
      });

      setFeedbackContent('');
      setFeedbackType('comment');
    } catch (error) {
      console.error('Failed to add feedback:', error);
      alert('Failed to add feedback');
    }
  };

  const handleSignatureComplete = async (signaturePayload) => {
    try {
      // signaturePayload includes: signatureData, signerName, signerEmail, metadata
      await onSignDocument({
        documentId: document._id,
        signatureData: signaturePayload.signatureData,
        signerName: signaturePayload.signerName,
        signerEmail: signaturePayload.signerEmail,
        metadata: signaturePayload.metadata,
      });

      setShowSignatureModal(false);
    } catch (error) {
      console.error('Failed to sign document:', error);
      alert('Failed to sign document. Please try again.');
    }
  };

  // Calculate workflow progress from client perspective
  const workflowSteps = [
    { label: 'Document Created', status: 'complete' },
    { label: 'Review & Approval', status: 'complete' },
    { label: 'Final Version Ready', status: document.status !== 'approved' ? 'complete' : 'pending' },
    { label: 'Your Review', status: document.status === 'final_version_ready' ? 'current' : document.status === 'final_signature_complete' ? 'complete' : 'pending' },
    { label: 'Signature', status: document.finalSignatureStatus === 'signed' ? 'complete' : 'pending' },
  ];

  return (
    <div className="space-y-6">
      {/* Client-Facing Workflow */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-6">Your Document Status</h2>

        <div className="space-y-4">
          {workflowSteps.map((step, index) => (
            <div key={index} className="flex items-center gap-4">
              {/* Timeline dot */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    step.status === 'complete'
                      ? 'bg-green-100 text-green-700'
                      : step.status === 'current'
                      ? 'bg-blue-100 text-blue-700 animate-pulse'
                      : 'bg-secondary-200 text-secondary-700'
                  }`}
                >
                  {step.status === 'complete' ? <Check size={16} /> : index + 1}
                </div>
                {index < workflowSteps.length - 1 && (
                  <div
                    className={`w-0.5 h-8 mt-1 ${
                      step.status === 'complete' ? 'bg-green-200' : 'bg-secondary-200'
                    }`}
                  />
                )}
              </div>

              {/* Step info */}
              <div className="flex-1">
                <p
                  className={`font-medium ${
                    step.status === 'complete'
                      ? 'text-green-700'
                      : step.status === 'current'
                      ? 'text-blue-700'
                      : 'text-secondary-600'
                  }`}
                >
                  {step.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Final Document Section */}
      {document.status === 'final_version_ready' && (
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText size={24} className="text-primary-600" />
              <div>
                <h3 className="font-semibold text-secondary-900">Final Document Ready</h3>
                <p className="text-sm text-secondary-600">
                  Please review and sign the document below
                </p>
              </div>
            </div>
            <a
              href={document.finalVersion}
              download={`${document.title}.pdf`}
              className="btn-secondary btn-sm flex items-center gap-2"
            >
              <Download size={16} />
              Download
            </a>
          </div>

          {/* Document Preview */}
          <div className="border border-secondary-200 rounded-lg overflow-hidden bg-secondary-50">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="w-full p-4 flex items-center justify-between bg-secondary-50 hover:bg-secondary-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Eye size={18} className="text-secondary-600" />
                <span className="font-medium text-secondary-900">
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </span>
              </div>
            </button>

            {showPreview && (
              <div className="h-96 bg-white">
                {iframeLoading && (
                  <div className="h-full flex items-center justify-center text-secondary-600">
                    Loading preview...
                  </div>
                )}
                <iframe
                  src={`${document.finalVersion}#toolbar=0`}
                  className="w-full h-full"
                  title="Document Preview"
                  onLoad={() => setIframeLoading(false)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comments & Feedback Section */}
      {document.status === 'final_version_ready' && (
        <div className="card p-6 space-y-4">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <MessageSquare size={20} />
            Your Feedback & Changes
          </h3>

          {/* Add Feedback Form */}
          <form onSubmit={handleAddFeedback} className="space-y-3 border-b border-secondary-200 pb-4">
            <div>
              <label className="text-sm font-medium text-secondary-900 block mb-2">
                Feedback Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="comment"
                    checked={feedbackType === 'comment'}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="rounded"
                  />
                  <span className="text-sm text-secondary-700">Comment</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="change_request"
                    checked={feedbackType === 'change_request'}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="rounded"
                  />
                  <span className="text-sm text-secondary-700">Request Change</span>
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-secondary-900 block mb-2">
                {feedbackType === 'change_request' ? 'What needs to change?' : 'Add a comment'}
              </label>
              <textarea
                value={feedbackContent}
                onChange={(e) => setFeedbackContent(e.target.value)}
                placeholder={feedbackType === 'change_request' 
                  ? 'Describe the changes you need...' 
                  : 'Share your thoughts...'}
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !feedbackContent.trim()}
              className="btn-primary btn-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Send size={16} />
              Submit Feedback
            </button>
          </form>

          {/* Display Existing Feedback */}
          {document.clientFeedback && document.clientFeedback.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-secondary-700">Your Comments</h4>
              {document.clientFeedback.map((feedback, index) => (
                <div key={index} className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-xs font-medium text-secondary-600">
                        {new Date(feedback.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-secondary-200 text-secondary-700">
                      {feedback.type === 'change_request' ? 'Change Request' : 'Comment'}
                    </span>
                  </div>
                  <p className="text-sm text-secondary-900">{feedback.content}</p>
                </div>
              ))}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle size={18} className="text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-900">
              All comments and change requests are visible to our team and will be addressed if the document needs revision.
            </p>
          </div>
        </div>
      )}

      {/* Signature Section */}
      {document.status === 'final_version_ready' && document.finalSignatureStatus !== 'signed' && (
        <div className="card p-6 space-y-4 border border-primary-200 bg-primary-50">
          <div className="flex items-center gap-3">
            <Check size={24} className="text-primary-600" />
            <div>
              <h3 className="font-semibold text-primary-900">Ready to Sign?</h3>
              <p className="text-sm text-primary-800">
                Once you're satisfied with the final version, sign below to complete this document.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowSignatureModal(true)}
            disabled={isLoading}
            className="btn-primary w-full disabled:opacity-50"
          >
            Sign Document
          </button>
        </div>
      )}

      {/* Signature Complete */}
      {document.finalSignatureStatus === 'signed' && (
        <div className="card p-6 space-y-4 border border-green-200 bg-green-50">
          <div className="flex items-center gap-3">
            <Check size={24} className="text-green-600" />
            <div>
              <h3 className="font-semibold text-green-900">Document Signed</h3>
              <p className="text-sm text-green-800">
                Thank you! Your signature has been recorded.
                {document.finalSignedAt && (
                  <> This document was signed on {new Date(document.finalSignedAt).toLocaleDateString()}</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 p-4 flex items-center justify-between">
              <h2 className="font-semibold text-secondary-900">Sign Document</h2>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-secondary-500 hover:text-secondary-700"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <EnhancedSignatureCapture
                signerInfo={{
                  name: clientName,
                  email: clientEmail,
                }}
                documentTitle={document.title}
                requiresMultipleSigners={false}
                signerSequence={1}
                totalSigners={1}
                onSignatureComplete={handleSignatureComplete}
                onCancel={() => setShowSignatureModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
